import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import SearchFilters from '../../components/Search/SearchFilters';
import videoService from '../../services/video.service';
import { Video } from '../../types';

const SearchVideos: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [filteredVideos, setFilteredVideos] = useState<Video[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load public videos
  useEffect(() => {
    const loadVideos = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await videoService.getAvailableVideos();
        setVideos(data);

        // Extract unique tags
        const tags = new Set<string>();
        data.forEach((video: Video) => {
          video.tags?.forEach((tag: string) => tags.add(tag));
        });
        setAvailableTags(Array.from(tags).sort());
      } catch (err) {
        console.error('Error loading videos:', err);
        setError('Erreur lors du chargement des vidéos');
      } finally {
        setLoading(false);
      }
    };

    loadVideos();
  }, []);

  // Filter videos
  useEffect(() => {
    let filtered = [...videos];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (video) =>
          video.title?.toLowerCase().includes(query) ||
          video.description?.toLowerCase().includes(query)
      );
    }

    // Filter by tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter((video) =>
        selectedTags.every((tag) => video.tags?.includes(tag))
      );
    }

    setFilteredVideos(filtered);
  }, [searchQuery, selectedTags, videos]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - Search Filters */}
        <Grid item xs={12} md={3}>
          <SearchFilters
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            availableTags={availableTags}
            contentType="vidéos"
          />
        </Grid>

        {/* Right Panel - Results */}
        <Grid item xs={12} md={9}>
          <Box>
            {/* Results count */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filteredVideos.length} vidéo{filteredVideos.length > 1 ? 's' : ''} trouvée{filteredVideos.length > 1 ? 's' : ''}
            </Typography>

            {/* Results */}
            <Grid container spacing={2}>
              {filteredVideos.map((video) => (
                <Grid item xs={12} sm={6} md={4} key={video.id}>
                  <Card>
                    {video.thumbnail_url && (
                      <CardMedia
                        component="img"
                        height="200"
                        image={video.thumbnail_url}
                        alt={video.title}
                        sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                      />
                    )}
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {video.title}
                      </Typography>

                      {video.created_by_name && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Par {video.created_by_name}
                        </Typography>
                      )}

                      {video.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            mb: 1,
                          }}
                        >
                          {video.description}
                        </Typography>
                      )}

                      {video.duration_seconds && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Durée: {Math.floor(video.duration_seconds / 60)}:{String(video.duration_seconds % 60).padStart(2, '0')}
                        </Typography>
                      )}

                      {video.tags && video.tags.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                          {video.tags.map((tag) => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {filteredVideos.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  Aucune vidéo ne correspond à vos critères de recherche
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SearchVideos;
